// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Controlled Substance Badge
// DEA Schedule indicator for controlled substances
// ============================================================================

import { clsx } from 'clsx';
import type { DEASchedule } from '@/types';

interface ControlledSubstanceBadgeProps {
  schedule: DEASchedule;
  showLabel?: boolean;
  className?: string;
}

export function ControlledSubstanceBadge({
  schedule,
  showLabel = false,
  className,
}: ControlledSubstanceBadgeProps) {
  if (schedule === null) {
    return null;
  }

  const scheduleLabels: Record<string, string> = {
    'CII': 'C-II',
    'CIII': 'C-III',
    'CIV': 'C-IV',
    'CV': 'C-V',
  };

  const scheduleColors: Record<string, string> = {
    'CII': 'bg-red-600 text-white',
    'CIII': 'bg-orange-500 text-white',
    'CIV': 'bg-amber-500 text-white',
    'CV': 'bg-yellow-500 text-black',
  };

  const label = scheduleLabels[schedule] || schedule;
  const colorClass = scheduleColors[schedule] || 'bg-gray-500 text-white';

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-bold',
        colorClass,
        className
      )}
      title={`DEA Schedule ${schedule} Controlled Substance`}
    >
      {label}
      {showLabel && (
        <span className="ml-1 font-normal">Controlled</span>
      )}
    </span>
  );
}

// Additional info component for C-II special handling
export function ControlledSubstanceWarning({
  schedule,
}: {
  schedule: DEASchedule;
}) {
  if (schedule !== 'CII') {
    return null;
  }

  return (
    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm">
      <div className="flex items-start gap-2">
        <span className="text-red-600 text-lg">⚠️</span>
        <div>
          <p className="font-semibold text-red-800">Schedule II Requirements</p>
          <ul className="mt-1 text-red-700 list-disc list-inside space-y-1">
            <li>Double count required by pharmacist</li>
            <li>Original written prescription required</li>
            <li>No refills permitted</li>
            <li>Perpetual inventory update required</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
